import Icon from "@/icons";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import userPresences from "@/presences";
import permissions from "@/permissions";
import { UserPopover } from "@/components/Popover/User";
import { InviteTeam } from "@/components/Dialogs/InviteToTeam";
import BotTag from "@/components/Tags/Bot";
import { ManageMember } from "@/components/ContextMenu/Member";
import { Role, User } from "types";
import { useCallback, useEffect, useState } from "react";
import getPermissionsById from "@/components/Dialogs/ChannelSettings/getPermissions";
import ErrorBoundary from "@/ErrorBoundary";
import { useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
export default function MembersSidebar({ isOpen, channel }: { isOpen: any, channel: any }) {
    const queryClient = useQueryClient();
    const { teamId } = useParams<{ teamId: string }>();
    const teamDetails = queryClient.getQueryState<any>(["team", teamId] as any);
    const members = queryClient.getQueryData<any>(["team", teamId, "members"] as any);
    const rolesData = queryClient.getQueryState<{ roles: Role[] }>(["team", teamId, "roles"])!.data as { roles: Role[] };
    const roles = rolesData?.roles || [];
    const me = queryClient.getQueryData<any>(["team", teamId, "members", "@me"])
    if (!members) {
        return (
            <div className="top-0 right-0 min-w-72 max-w-72 h-screen hidden sm:flex flex-col bg-brand-dark scrollbar-thin scrollbar-thumb-gray-500 scrollbar-track-gray-800 overflow-y-auto" style={{ width: "304px", scrollbarWidth: "none" }}>
                <div className="px-3 py-6 h-full">
                    <div className="rounded-lg bg-brand-darkest h-full px-2 py-4">
                        <div className="flex flex-col justify-center items-center w-full h-32"></div>
                    </div>
                </div>
            </div>
        );
    }



    const getHighestRole = useCallback((member: any) => {
        if (!member.roles) return null;
        const rolePriorities = (member.roles || [])
            .map((roleId: string) => roles.find((role: any) => role.id === roleId && role.isDisplayedSeparately)?.priority)
            .filter((priority: number | undefined) => priority !== undefined);

        return Math.max(...rolePriorities);
    }, [roles]);

    const [usersByRole, setUsersByRole] = useState<Map<string, User[]>>(new Map());

    useEffect(() => {
        const roleMap = new Map();
        const sortedRoles = [...roles].sort((a, b) => b.priority - a.priority);

        sortedRoles.forEach((role) => {
            const membersWithRole = members.members.filter((member: User) => {
                const userHighRolePriority = getHighestRole(member);
                console
                return member.presence !== 0 && userHighRolePriority === role.priority && (

                    getPermissionsById({
                        teamData: teamDetails as any,
                        userId: member.id,
                        channelData: channel,
                        members: members,
                        roles: roles,
                    }).allow.includes("VIEW_CHANNEL"))
            });
            if (membersWithRole.length > 0) roleMap.set(role.id, membersWithRole);
        });

        setUsersByRole(roleMap);
    }, [roles, members, channel]);


    const [onlineMembers, setOnlineMembers] = useState<any[]>([]);

    const [offlineMembers, setOfflineMembers] = useState<any[]>([]);

    useEffect(() => {
        if (members?.members) {
            const onlineMembers = members.members.filter((member: { presence: number; }) => member.presence !== 0).filter((member: any) => !Array.from(usersByRole.values()).flat().includes(member))
                .filter((member: any) => {
                    const permissions = getPermissionsById({
                        teamData: teamDetails as any,
                        userId: member.id,
                        channelData: channel,
                        members: members,
                        roles: roles,
                    });
                    return permissions.allow.includes("VIEW_CHANNEL");
                }) || []
            const offlineMembers = members.members.filter((member: { presence: number; }) => member.presence === 0)
                .filter((member: any) => {
                    const permissions = getPermissionsById({
                        teamData: teamDetails as any,
                        userId: member.id,
                        channelData: channel,
                        members: members,
                        roles: roles,
                    });
                    return permissions.allow.includes("VIEW_CHANNEL");
                })
                || []
            setOnlineMembers(onlineMembers);
            setOfflineMembers(offlineMembers);
        }
    }, [members, channel, roles, usersByRole]);

    const [dialogOpenUser, setDialogOpenUser] = useState<string | null>(null);
    return (
        <div className="sm:p-4 flex h-full">
            <div className={`min-w-72 shadow-2xl max-w-72 sm:rounded-2xl z-20 ${isOpen === 0 ? ' right-0 z-10 pb-14 sm:pb-0 flex' : 'hidden sm:flex w-full'} flex-col bg-brand-dark scrollbar-thin scrollbar-thumb-gray-500 scrollbar-track-gray-800 overflow-y-auto`} style={{ width: "304px", scrollbarWidth: "none" }}>
                <div className="px-2 py-1 h-full">
                    <div className="rounded-lg bg-brand-dark h-full px-2 py-4">
                        {!members?.members || !me?.member || !teamDetails?.data?.team ? (
                            <div className="flex flex-col gap-2 p-2 rounded-lg hover:bg-brand-darkest cursor-pointer">
                                <div className="flex items-start gap-2 p-2 rounded-lg hover:bg-brand-darkest cursor-pointer">
                                    <div className="w-8 h-8 animate-pulse rounded-lg bg-gray-600" />
                                    <div className="w-36 h-8 animate-pulse rounded-lg bg-gray-600" />
                                </div>
                            </div>
                        ) : (
                            <div className="flex flex-col gap-2 p-2 py-2">
                                <ErrorBoundary>
                                    <div className="items-start flex flex-col gap-2">
                                        <div className="flex justify-between w-full">

                                            <p className="font-semibold text-gray-300 opacity-50">Members</p>
                                            {me.member && permissions && permissions.get("CREATE_INVITES") && (
                                                <InviteTeam team={teamDetails?.data?.team?.id} key={teamDetails?.data?.team?.id + "-invite"}>
                                                    <span id={teamDetails?.data?.team?.id} className="flex items-center bg-brand-back justify-center px-2 h-8 w-8 rounded-full transition-all duration-300">
                                                        <Icon name="plus" size="36" color="white" />
                                                    </span>
                                                </InviteTeam>
                                            )}
                                        </div>
                                        {Array.from(usersByRole).map(([roleId, users], index) => (

                                            <div key={`${roleId}-${index}`} className="flex flex-col gap-2 w-full">
                                                <h1 className="font-semibold text-sm text-gray-300">{roles.find((r) => r.id === roleId)?.name} - {users.length}</h1>
                                                {users.map((member: any) => (
                                                    <ManageMember key={member.id} member={member}>
                                                        <Member member={member} me={me.member} roles={roles} isOffline={false} teamDetails={teamDetails} setDialogOpenUser={setDialogOpenUser} dialogOpenUser={dialogOpenUser} index={index} />
                                                    </ManageMember>
                                                ))}
                                            </div>
                                        ))}
                                        {onlineMembers.length > 0 && (<h1 className="text-gray-400 font-semibold text-sm">Online - {onlineMembers.length}</h1>)}
                                        {onlineMembers.map((member: any, index: any) => (
                                            <ManageMember key={`${member.id}-${index}-modal`} member={member}>
                                                <Member member={member} me={me.member} roles={roles} isOffline={false} teamDetails={teamDetails} setDialogOpenUser={setDialogOpenUser} dialogOpenUser={dialogOpenUser} index={index} />
                                            </ManageMember>
                                        ))}
                                        {offlineMembers.length > 0 && (<h1 className="text-gray-400 font-semibold text-sm">Offline - {offlineMembers.length}</h1>)}
                                        {offlineMembers.map((member: any, index: any) =>
                                            <ManageMember key={`${member.id}-${index}-modal`} member={member}>
                                                <Member member={member} me={me.member} roles={roles} isOffline={true} teamDetails={teamDetails} setDialogOpenUser={setDialogOpenUser} dialogOpenUser={dialogOpenUser} index={index} />
                                            </ManageMember>
                                        )}

                                    </div>
                                </ErrorBoundary>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

function Member({ member, me, roles, isOffline, teamDetails, setDialogOpenUser, dialogOpenUser, index }: { member: any, me: any, roles: any, isOffline: boolean, teamDetails: any, setDialogOpenUser: any, dialogOpenUser: any, index: any }) {
    const getHighestRole = useCallback(() => {
        if (!member.roles) return null;
        const rolePriorities = (member.roles || [])
            .map((roleId: string) => roles.find((role: any) => role.id === roleId && role.isDisplayedSeparately)?.priority)
            .filter((priority: number | undefined) => priority !== undefined);

        return roles.find((role: any) => role.priority === Math.max(...rolePriorities));
    }, [roles, member.roles]) as any;

    const highestRole = getHighestRole();
    const highestRoleColor = highestRole?.color || "white";
    return (
        <div key={`${member.id}-${index}`} className="w-full">
            <UserPopover user={member} key={`${member.id}-popover`} customIsOpen={dialogOpenUser === member.id} setDialogOpenUser={setDialogOpenUser}>
                <div className={`flex items-center gap-2 w-full p-2 rounded-lg hover:bg-brand-darkest group cursor-pointer ${isOffline ? "opacity-50" : "text-white"}`}>
                    <div className="w-8 h-8 rounded-lg relative">
                        <Avatar className="bg-gray-600 min-w-8 min-h-8 w-full h-full">
                            {member.profilePicture && (
                                <>
                                    <AvatarImage src={member.profilePicture.replace("webp", "png")} className="rounded-full group-hover:hidden" />
                                    <AvatarImage src={member.profilePicture} className="rounded-full group-hover:block" />
                                </>
                            ) || (
                                    <AvatarFallback className="text-sm">{member.username[0]}</AvatarFallback>
                                )}
                        </Avatar>
                        <div className="absolute bottom-0 right-0 w-3 h-3 rounded-full border-2 border-brand-darkest" style={{ backgroundColor: userPresences.get(member.presence) }} />
                    </div>
                    <div className="flex gap-2 relative items-center">
                        <div className="font-semibold text-lg relative" style={{ color: highestRoleColor }}>
                            {member.username}
                            {highestRole?.color2 && (
                                <span className='GradientRole-ColorFix' style={{
                                    color: highestRole?.color2,
                                }}>
                                    {member.username}
                                </span>
                            )}
                        </div>
                        <BotTag user={member} />
                    </div>
                </div>
            </UserPopover>
        </div>
    );
}